import async, { QueueObject } from 'async';
import { injectable } from 'inversify';
import { action, computed, makeObservable, observable } from 'mobx';
import { IConfiguration } from '../../../configuration/config.types';
import { ICurrentWorkspaceStore } from '../../../modules/workspaces/currentWorkspace/CurrentWorkspace.store';
import { doNothing } from '../../helpers/function.helpers';
import { ACDLError, Failed, ForbiddenError, OperationResult, PreprocessingError, UnauthorizedError, UploadSuccess } from '../../models/error.model';
import { IApiService } from '../../services/api.service.base';
import { IBrowserService } from '../../services/browser.service';
import { ICryptoService } from '../../services/crypto.service';
import { IUploadsHubService } from '../../services/uploadsHub.service';
import {
  DatasetUploadAbortedNotification,
  DatasetUploadCompletedNotification,
  DatasetUploadFailedNotification,
  NotificationLevel,
  PolicyLimitExceededNotification,
  ToastNotification,
} from '../notifications/models/notification.model';
import { INotificationsService } from '../notifications/services/notifications.service';
import { Plan } from '../user/user.store';

const datasetNotFoundError = 'Uploading dataset not found';

export interface WillImageUploadExceedsLimitResult {
  willExceedsImageAmount: boolean;
  willExceedsAvailableSpace: boolean;
  areAllSuccessful: boolean;
  ownerPlan: Plan;
}

enum UploadStatus {
  Waiting = 'Waiting',
  Uploading = 'Uploading',
  Uploaded = 'Uploaded',
  Canceled = 'Canceled',
  Paused = 'Paused',
  Interrupted = 'Interrupted',
}

interface IItemPayload<T> {
  datasetId: string;
  item: T;
}

export interface IUploadedImage {
  imageSize: number;
}

export interface IDatasetUpload<T> {
  datasetId: string;
  datasetName: string;
  items: T[];
  uploadedItemsCount: number;
  uploadedItemsSize: number;
  allItemsCount: number;
  status: UploadStatus;
  rejectedItems: T[];
  hasAnyError: boolean;
}

export interface IDatasetUploadService<T> {
  datasetsQueue: IDatasetUpload<T>[];
  addDatasetToQueueAsync(items: T[], datasetId: string, datasetName: string): Promise<void>;
  cancelUpload: (datasetId: string) => Promise<void>;
  cancelUploads(): void;
  pauseUpload: (datasetId: string) => void;
  restoreUpload: (datasetId: string) => void;
  onUploadFinishedSuccessfully: ((datasetId: string) => void | Promise<void>)[];
  onUploadAborted: ((datasetId: string) => void | Promise<void>)[];
  onDatasetUploadProgress: ((datasetId: string, imageSize: number) => void)[];
}

@injectable()
export abstract class UploadServiceBase<T> implements IDatasetUploadService<T> {
  datasetsQueue: IDatasetUpload<T>[] = [];
  imagesQueue?: QueueObject<IItemPayload<T>>;
  onUploadFinishedSuccessfully: ((datasetId: string) => void)[] = [];
  onUploadAborted: ((datasetId: string) => void)[] = [];
  onDatasetUploadProgress: ((datasetId: string, imageSize: number) => void)[] = [];

  constructor(
    private readonly browserService: IBrowserService,
    protected readonly notificationService: INotificationsService,
    protected readonly apiServiceImageUpload: IApiService,
    protected readonly configuration: IConfiguration,
    protected readonly uploadsHub: IUploadsHubService,
    protected readonly cryptoService: ICryptoService,
    protected readonly currentWorkspaceStore: ICurrentWorkspaceStore
  ) {
    this.browserService.addOnBeforeUnload(() => this.datasetsQueue.length > 0);

    makeObservable(this, {
      datasetsQueue: observable,
      uploadingDataset: computed,
      addDatasetToQueueAsync: action.bound,
      uploadItemAsync: action.bound,
      uploadDataset: action.bound,
      pauseUpload: action.bound,
      restoreUpload: action.bound,
      finishUpload: action.bound,
      cancelUpload: action.bound,
      cancelUploads: action.bound,
      cleanQueueAndSendNotification: action.bound,
      onAllItemsUploaded: action.bound,
      tryUploadNextDataset: action.bound,
    });
  }

  get uploadingDataset(): IDatasetUpload<T> | undefined {
    return this.datasetsQueue.find((ds) => ds.status === UploadStatus.Uploading || ds.status === UploadStatus.Paused);
  }

  async addDatasetToQueueAsync(items: T[], datasetId: string, datasetName: string): Promise<void> {
    this.datasetsQueue.push({
      datasetId,
      datasetName,
      items,
      allItemsCount: items.length,
      rejectedItems: [],
      status: UploadStatus.Waiting,
      uploadedItemsCount: 0,
      uploadedItemsSize: 0,
      hasAnyError: false,
    });
    await this.uploadsHub.initializeAsync();
    await this.uploadsHub.startUploadAsync(datasetId);
    this.tryUploadNextDataset();
  }

  async uploadItemAsync(payload: IItemPayload<T>, onUploadItemFinished: () => void): Promise<void> {
    const result = await this.upload(payload.item, payload.datasetId);
    const dataset = this.datasetsQueue.find((ds) => ds.datasetId === payload.datasetId);
    if (!dataset) {
      return;
    } // post request finished after dataset was canceled
    dataset.uploadedItemsCount += 1;
    if (result.isSuccess) {
      const imageSize = (result as UploadSuccess).imageSize;
      dataset.uploadedItemsSize += imageSize;
      this.notifyOnImageUploadFinished(dataset, imageSize);
    } else {
      await this.handleUploadError(payload.datasetId, result as Failed, payload.item);
      dataset.hasAnyError = true;
      dataset.rejectedItems.push(payload.item);
    }
    onUploadItemFinished(); // required to run 'drain' method
  }

  protected async handleUploadError(datasetId: string, result?: Failed, _payload?: T): Promise<void> {
    if (result) {
      if (result.Error instanceof ForbiddenError || result instanceof UnauthorizedError) {
        await this.cancelUpload(datasetId, UploadStatus.Interrupted);
        result.isHandled = true;
      }
      if (result.Error instanceof PreprocessingError) {
        if ((result.Error.cause as Error).message === 'Not supported file type') {
          await this.notificationService.push(
            new ToastNotification(NotificationLevel.ERROR, {
              template: 'not_supported_file_type_x',
              data: { type: (result.Error.cause as Error).cause ?? 'Unknown' },
            })
          );
        } else {
          await this.notificationService.push(new ToastNotification(NotificationLevel.ERROR, { template: 'cannot_preprocess_file' }));
        }
        result.isHandled = true;
      }
      if (result.Error?.isBadRequestWithCode(['ADDING_IMAGE_NOT_ALLOWED_NOT_ENOUGH_FUNDS'])) {
        await this.notificationService.push(new ToastNotification(NotificationLevel.ERROR, { template: 'upload_limit_is_exceeded' }));
        await this.cancelUpload(datasetId, UploadStatus.Interrupted);
        result.isHandled = true;
      }
      if (result.Error?.isBadRequestWithCode(['ADDING_IMAGE_NOT_ALLOWED'])) {
        await this.notificationService.push(
          new ToastNotification(NotificationLevel.ERROR, { template: 'you_do_not_have_permisions_to_upload_to_this_dataset' })
        );
        await this.cancelUpload(datasetId, UploadStatus.Interrupted);
        result.isHandled = true;
      }
      if (result.Error?.isBadRequestWithCode(['UPLOAD_IN_PUBLISHED_NOT_ALLOWED'])) {
        await this.notificationService.push(new ToastNotification(NotificationLevel.ERROR, { template: 'upload_to_published_dataset_is_not_allowed' }));
        await this.cancelUpload(datasetId, UploadStatus.Interrupted);
        result.isHandled = true;
      }
      if (result.Error?.isBadRequestWithCode(['POLICY_LIMITS_EXCEEDED'])) {
        await this.notificationService.push(new PolicyLimitExceededNotification());
        await this.cancelUpload(datasetId, UploadStatus.Interrupted);
        result.isHandled = true;
      }
    }

    if (!result || !result.isHandled) {
      await this.notificationService.push(new ToastNotification(NotificationLevel.ERROR, { template: 'something_went_wrong' }));
    }
  }

  uploadDataset(datasetUpload: IDatasetUpload<T>): void {
    datasetUpload.status = UploadStatus.Uploading;
    this.imagesQueue = async.queue(
      (p, c) => {
        void this.uploadItemAsync(p, c);
      },
      (this.configuration.appConfig && this.configuration.appConfig.concurrentUploadsCount) || 1
    );
    this.imagesQueue.drain(() => {
      this.onAllItemsUploaded();
    });

    datasetUpload.items.forEach((item) => this.imagesQueue?.push({ item, datasetId: datasetUpload.datasetId }, doNothing));
  }

  pauseUpload(datasetId: string): void {
    if (!this.uploadingDataset) {
      throw new ACDLError(datasetNotFoundError, undefined);
    }
    if (this.uploadingDataset.datasetId !== datasetId) {
      return;
    }

    this.uploadingDataset.status = UploadStatus.Paused;
    this.imagesQueue?.pause();
  }

  restoreUpload(datasetId: string): void {
    if (!this.uploadingDataset) {
      throw new ACDLError(datasetNotFoundError, undefined);
    }
    if (this.uploadingDataset.datasetId !== datasetId) {
      return;
    }

    this.uploadingDataset.status = UploadStatus.Uploading;
    this.imagesQueue?.resume();

    const lastItemAlreadyUploaded = this.imagesQueue?.idle();
    if (lastItemAlreadyUploaded) {
      this.finishUpload(this.uploadingDataset.datasetId);
    }
  }

  async cancelUpload(datasetId: string, uploadStatus: UploadStatus = UploadStatus.Canceled): Promise<void> {
    if (this.isDatasetUploadAborted(datasetId)) {
      return;
    }
    if (this.uploadingDataset && this.uploadingDataset.datasetId === datasetId) {
      this.imagesQueue?.kill();
      this.imagesQueue = undefined;
    }

    const dataset = this.datasetsQueue.find((d) => d.datasetId === datasetId);
    if (!dataset) {
      return;
    }

    dataset.status = uploadStatus;

    this.onUploadAborted.forEach((f) => {
      if (f) {
        f(dataset.datasetId);
      }
    });

    await this.notifyAboutDatasetUploadFinishAsync(dataset);
    this.removeDatasetFromQueue(dataset.datasetId);
    this.tryUploadNextDataset();
  }

  cancelUploads(): void {
    this.datasetsQueue = [];
    if (this.imagesQueue) {
      this.imagesQueue.kill();
      this.imagesQueue = undefined;
    }
  }

  tryUploadNextDataset(): void {
    if (this.uploadingDataset) {
      return;
    }

    const firstWaitingDataset = this.datasetsQueue.find((ds) => ds.status === UploadStatus.Waiting);
    if (!firstWaitingDataset) {
      return;
    }

    this.uploadDataset(firstWaitingDataset);
  }

  onAllItemsUploaded(): void {
    const uploadingDataset = this.uploadingDataset;
    if (!uploadingDataset) {
      throw new ACDLError('Uploading dataset not found', undefined);
    }

    if (uploadingDataset.status === UploadStatus.Paused) {
      return;
    }

    this.finishUpload(uploadingDataset.datasetId);
  }

  removeDatasetFromQueue(datasetId: string): void {
    this.datasetsQueue = this.datasetsQueue.filter((q) => q.datasetId !== datasetId);
  }

  finishUpload(datasetId: string): void {
    const dataset = this.datasetsQueue.find((d) => d.datasetId === datasetId);
    if (!dataset) {
      throw new ACDLError(`Dataset with id ${datasetId} not found`, undefined);
    }

    if (this.uploadingDataset?.datasetId === datasetId) {
      this.imagesQueue?.kill();
      this.imagesQueue = undefined;
    }

    dataset.status = UploadStatus.Uploaded;
    setTimeout(() => {
      void this.cleanQueueAndSendNotification(dataset);
    }, 100);

    this.tryUploadNextDataset();
  }

  async cleanQueueAndSendNotification(dataset: IDatasetUpload<T>): Promise<void> {
    this.removeDatasetFromQueue(dataset.datasetId);
    this.onUploadFinishedSuccessfully.forEach((f) => {
      if (f) {
        f(dataset.datasetId);
      }
    });
    await this.notifyAboutDatasetUploadFinishAsync(dataset);
  }

  async notifyAboutDatasetUploadFinishAsync(dataset: IDatasetUpload<T>): Promise<void> {
    await this.uploadsHub.finishUploadAsync(dataset.datasetId);
    if (dataset.status === UploadStatus.Canceled) {
      await this.notificationService.push(
        new ToastNotification(
          NotificationLevel.INFO,
          { template: 'dataset_upload_canceled' },
          { template: 'upload_of_x_canceled', data: { datasetName: dataset.datasetName } }
        )
      );
    } else if (dataset.rejectedItems.length === dataset.allItemsCount) {
      await this.notificationService.push(new DatasetUploadFailedNotification(dataset.datasetName));
    } else if (dataset.status === UploadStatus.Interrupted) {
      await this.notificationService.push(new DatasetUploadAbortedNotification(dataset.datasetName));
    } else if (dataset.rejectedItems.length > 0) {
      await this.datasetUploadCompletedWithInfoNotification(dataset);
    } else {
      await this.notificationService.push(new DatasetUploadCompletedNotification(dataset.datasetName));
    }
  }

  isDatasetUploadAborted = (datasetId: string): boolean => {
    const dataset = this.datasetsQueue.find((d) => d.datasetId === datasetId);
    return !dataset || dataset.status === UploadStatus.Canceled || dataset.status === UploadStatus.Interrupted;
  };

  notifyOnImageUploadFinished(dataset: IDatasetUpload<T>, imageSize: number): void {
    this.onDatasetUploadProgress.forEach((c: (datasetId: string, imageSize: number) => void) => {
      c(dataset.datasetId, imageSize);
    });
  }

  abstract upload(item: T, datasetId: string): Promise<OperationResult>;

  abstract datasetUploadCompletedWithInfoNotification(dataset: IDatasetUpload<T>): Promise<void>;
}
